import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Task, OrgUserWithRole } from '@property/shared';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useKeyboardHeight } from '@/hooks/use-keyboard-height';
import { useAssignTask } from '@/hooks/use-task-mutations';
import { AssigneeSearchInput } from '@/components/assignee-search-input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface ReassignTaskModalProps {
  task: Task | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ReassignBody({
  selectedUser,
  onSelect,
  onClear,
  onConfirm,
  isPending,
  dropdownDirection = 'down',
}: {
  selectedUser: OrgUserWithRole | null;
  onSelect: (user: OrgUserWithRole) => void;
  onClear: () => void;
  onConfirm: () => void;
  isPending: boolean;
  dropdownDirection?: 'up' | 'down';
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Select an agent to reassign this task to.</p>
      <AssigneeSearchInput
        selectedUser={selectedUser}
        onSelect={onSelect}
        onClear={onClear}
        dropdownDirection={dropdownDirection}
      />
      <Button disabled={!selectedUser || isPending} onClick={onConfirm} className="w-full">
        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Confirm Reassignment
      </Button>
    </div>
  );
}

export function ReassignTaskModal({ task, open, onOpenChange }: ReassignTaskModalProps) {
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const keyboardHeight = useKeyboardHeight();
  const [selectedUser, setSelectedUser] = useState<OrgUserWithRole | null>(null);
  const { mutate: assignTask, isPending } = useAssignTask();

  useEffect(() => {
    if (!open) setSelectedUser(null);
  }, [open]);

  function handleConfirm() {
    if (!task || !selectedUser) return;
    assignTask(
      { taskId: task.id, agentId: selectedUser.id },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  const bodyProps = {
    selectedUser,
    onSelect: setSelectedUser,
    onClear: () => setSelectedUser(null),
    onConfirm: handleConfirm,
    isPending,
  };

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[440px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Reassign Task</DialogTitle>
          </DialogHeader>
          <ReassignBody {...bodyProps} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        style={{ bottom: keyboardHeight }}
        className="rounded-t-2xl max-h-[85dvh] overflow-y-auto overflow-x-hidden pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      >
        {/* drag handle */}
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-muted-foreground/30" />
        <SheetHeader className="mb-4 text-left">
          <SheetTitle>Reassign Task</SheetTitle>
        </SheetHeader>
        <ReassignBody {...bodyProps} dropdownDirection="up" />
      </SheetContent>
    </Sheet>
  );
}
